import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { apiService } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";

interface DealerActivationPaymentProps {
  onError?: (error: string) => void;
}

const ACTIVATION_FEE_NET = 390;

const DealerActivationPayment: React.FC<DealerActivationPaymentProps> = ({
  onError,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const hasStartedRef = useRef(false);

  useEffect(() => {
    // Guard: only run once
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;

    const startActivation = async () => {
      try {
        // Step 1: Validate VAT ID if dealer has one saved
        let vatValidationStatus: "valid" | "invalid" | "idle" = "idle";
        const savedVatId: string | undefined = (user as any)?.vatId;

        if (savedVatId && savedVatId.trim() !== "") {
          try {
            await apiService.post("/dealer-receipts/validate-vat", {
              vatId: savedVatId,
            });
            vatValidationStatus = "valid";
          } catch {
            vatValidationStatus = "invalid";
          }
        }

        // Step 2: Calculate VAT based on validation result
        const vatIdToSend =
          vatValidationStatus === "valid"
            ? savedVatId
            : vatValidationStatus === "invalid"
              ? null
              : undefined;

        const vatResponse = await apiService.post<{
          success: boolean;
          data: {
            grossAmount: number;
            vatPercent: number;
            vatAmount: number;
            netAmount: number;
          };
        }>("/dealer-receipts/calculate-vat-preview", {
          netAmount: ACTIVATION_FEE_NET,
          vatId: vatIdToSend,
        });

        const vatData =
          (vatResponse as any).data?.data || (vatResponse as any).data;
        const finalAmount: number = vatData?.grossAmount ?? ACTIVATION_FEE_NET;

        // Step 3: Create Stripe Checkout session
        const checkoutResponse = await apiService.post<{
          success: boolean;
          checkoutUrl: string;
          sessionId: string;
        }>("/dealer/activation/payment/create-checkout", {
          amount: finalAmount,
          currency: "EUR",
          description: "Dealer Account Activation Fee",
          metadata: {
            source: "dealer_activation",
            netAmount: ACTIVATION_FEE_NET,
            vatId: vatValidationStatus === "valid" ? savedVatId : null,
            vatPercent: vatData?.vatPercent ?? 0,
          },
        });

        const checkoutUrl =
          (checkoutResponse as any).data?.checkoutUrl ||
          (checkoutResponse as any).checkoutUrl;

        if (!checkoutUrl) {
          throw new Error("Checkout URL not received from server");
        }

        // Step 4: Redirect to Stripe — no UI shown
        window.location.href = checkoutUrl;
      } catch (error: any) {
        const message =
          error.response?.data?.message || error.message || "Activation failed";
        onError?.(message);
        navigate("/activation-failed");
      }
    };

    startActivation();
  }, [user, navigate, onError]);

  // Full-screen spinner — user sees this briefly before Stripe opens
  return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="text-center space-y-6">
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary-600" />
        </div>
        <p className="text-lg font-medium text-secondary">
          {t("activation.activating")}
        </p>
        <p className="text-sm text-gray-500">
          {t("activation.activatingDescription")}
        </p>
      </div>
    </div>
  );
};

export default DealerActivationPayment;
